/**
 * A failed load that means the user can't open this encounter or appointment. Another
 * provider's record, another organization's record and a missing one all count, so the page shows
 * the same Access Denied screen for each and gives nothing away about which it was.
 * - Clinical routes say so with the clinicalAccess codes. Their other 404s, such as an
 *   encounter with no recording or transcript yet, carry other codes and are not refusals.
 * - GET /encounters/:id and /appointments/:id answer 403 or 404 without a code.
 */
const ACCESS_REFUSAL_CODES = new Set([
  'ENCOUNTER_ACCESS_DENIED',
  'ENCOUNTER_NOT_FOUND',
  'APPOINTMENT_ACCESS_DENIED',
  'APPOINTMENT_NOT_FOUND',
]);

const CLINICAL_LOAD = /\/v1\/clinical\/(encounter\/)?[^/]+\/(data|codes|summary)$/;
const RECORD_FETCH = /\/v1\/(encounters\/[^/]+|(ehr\/)?appointments\/[0-9a-f-]{36})$/i;

function pathOf(url: string): string {
  return url.split('?')[0];
}

/** The GETs an encounter or appointment page makes to load the record it opens. */
export function isRecordLoad(url: string): boolean {
  const path = pathOf(url);
  return CLINICAL_LOAD.test(path) || RECORD_FETCH.test(path);
}

export function isAccessRefusal(status: number | undefined, url: string, code?: string): boolean {
  if (status !== 403 && status !== 404) return false;
  if (code && ACCESS_REFUSAL_CODES.has(code)) return true;
  return RECORD_FETCH.test(pathOf(url));
}

export function isRecordAccessDenied(error: unknown): boolean {
  const e = error as { response?: { status?: number; data?: { code?: string } }; config?: { url?: string } } | null;
  if (!e?.response) return false;
  return isAccessRefusal(e.response.status, e.config?.url || '', e.response.data?.code);
}
